import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StorefrontIcon from "@mui/icons-material/Storefront";

const DiaViandaCard = ({
  dia,
  menus = [],
  pedido,
  onElegir,
  onCambiar,
  onCancelar,
  disabled,
  busy,
}) => {
  const tieneMenus = menus.length > 0;
  const bloqueadaPorFecha = dia.esPasado && !pedido;

  const handleClickMenu = (idMenu) => {
    if (disabled || busy || dia.esPasado) return;
    if (pedido) {
      if (pedido.id_menu === idMenu) return;
      onCambiar(pedido.id, idMenu);
    } else {
      onElegir(dia.fecha, idMenu);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: dia.esHoy ? "2px solid #1F618D" : "1px solid rgba(0,0,0,0.08)",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        height: "100%",
        bgcolor: dia.esPasado ? "rgba(0,0,0,0.02)" : "background.paper",
        opacity: dia.esPasado && !pedido ? 0.65 : 1,
        position: "relative",
        transition: "box-shadow .15s ease",
        "&:hover": {
          boxShadow: tieneMenus ? "0 4px 18px rgba(0,0,0,0.08)" : "none",
        },
      }}
    >
      {busy && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 4,
            zIndex: 2,
          }}
        >
          <CircularProgress size={26} />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1rem",
              textTransform: "capitalize",
            }}
          >
            {dia.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dia.diaMes}
          </Typography>
        </Box>
        {dia.esHoy && (
          <Chip
            label="HOY"
            size="small"
            color="primary"
            sx={{ fontWeight: 700, height: 20 }}
          />
        )}
      </Box>

      <Divider />

      {!tieneMenus && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 2,
            color: "text.secondary",
          }}
        >
          <EventBusyIcon sx={{ fontSize: 30, opacity: 0.5 }} />
          <Typography variant="body2" align="center">
            Sin menú cargado para este día
          </Typography>
        </Box>
      )}

      {tieneMenus && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
          {menus.map((m) => {
            const seleccionado = pedido?.id_menu === m.id_menu;
            return (
              <Box
                key={m.id_menu}
                onClick={() => handleClickMenu(m.id_menu)}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  p: 1,
                  borderRadius: 2.5,
                  border: seleccionado
                    ? "1.5px solid #2E7D32"
                    : "1px solid rgba(0,0,0,0.1)",
                  bgcolor: seleccionado
                    ? "rgba(46,125,50,0.08)"
                    : "transparent",
                  cursor: disabled || dia.esPasado ? "default" : "pointer",
                  opacity: disabled || dia.esPasado ? 0.7 : 1,
                  transition: "all .12s ease",
                  "&:hover": {
                    bgcolor:
                      !disabled && !dia.esPasado
                        ? seleccionado
                          ? "rgba(46,125,50,0.12)"
                          : "rgba(31,97,141,0.06)"
                        : undefined,
                  },
                }}
              >
                {seleccionado ? (
                  <CheckCircleIcon
                    sx={{ color: "#2E7D32", fontSize: 20, flexShrink: 0, mt: "1px" }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{
                      color: "rgba(0,0,0,0.25)",
                      fontSize: 20,
                      flexShrink: 0,
                      mt: "1px",
                    }}
                  />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: seleccionado ? 700 : 500,
                      lineHeight: 1.2,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {m.menu}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.5,
                      mt: 0.25,
                    }}
                  >
                    <StorefrontIcon
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        flexShrink: 0,
                        mt: "3px",
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {m.proveedor}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {tieneMenus && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 0.5,
          }}
        >
          {pedido ? (
            <Chip
              icon={<RestaurantIcon sx={{ fontSize: 15 }} />}
              label="Vianda elegida"
              size="small"
              sx={{
                bgcolor: "rgba(46,125,50,0.12)",
                color: "#1B5E20",
                fontWeight: 600,
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {bloqueadaPorFecha ? "Día pasado" : "Elegí una opción"}
            </Typography>
          )}

          {pedido && !disabled && !dia.esPasado && (
            <Tooltip title="Cancelar mi pedido">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onCancelar(pedido.id)}
                  disabled={busy}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )}
    </Card>
  );
};

export default DiaViandaCard;
